import * as Sentry from "@sentry/react";
import { Formio } from "formiojs";
import { render } from "react-dom";
import ReactGA from "react-ga4";
import { lazily } from "react-lazily";

import { ProductCollection } from "./ecommerce-video/ecommerce-video.types";
import { WidgetWrapper } from "./widget-wrapper";

const widgets = {
  Quiz: lazily(() => import("./quiz/widget.component")),
  MatchUp: lazily(() => import("./arcade-matchup/widget.component")),
  Puzzle: lazily(() => import("./arcade-puzzle/widget.component")),
  WordSearch: lazily(() => import("./word-search/word-search.component")),
  CplOfferPage: lazily(() =>
    import("./cpl-offer-page/cpl-offer-page.component")
  ),
  SocialReferralPage: lazily(() =>
    import("./social-referral/social-referral.component")
  ),
  LanderQuiz: lazily(() => import("./lander-quiz/lander-quiz.component")),
  LanderCplQuiz: lazily(() =>
    import("./lander-cpl-quiz/lander-cpl-quiz.component")
  ),
  Fkc: lazily(() => import("./fkc/fkc.component")),
  EcommerceVideo: lazily(() =>
    import("./ecommerce-video/ecommerce-video.component")
  ),
  Snhu: lazily(() => import("./snhu/snhu.component")),
  SmsOptin: lazily(() => import("./sms-optin/sms-optin.component")),
  RestaurantEquippers: lazily(() =>
    import("./restaurant-equippers/restaurant-equippers.component")
  ),
};

window.FKL.renderWidget = (widget, container) => {
  const { Widget } = widgets[widget];
  const {
    sentry_dsn_react_widgets,
    sentry_sample_rate,
    ga_tracking_code,
  } = window.FKL;

  ReactGA.initialize(ga_tracking_code);
  const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
  if (submission?.data?.email) {
    (async () => {
      const hashEmail = await window.theme.Utils.getHash(submission.data.email);
      ReactGA.set({ userId: hashEmail });
    })();
  }

  Sentry.init({
    dsn: sentry_dsn_react_widgets,
    sampleRate: sentry_sample_rate,
    release: "6cf0c0f1d5ce3cdb4fbac2dd1c5ba335a6934362",
    environment: "production",
  });

  render(
    <WidgetWrapper>
      <Widget />
    </WidgetWrapper>,
    container
  );
};

declare global {
  interface FKL {
    renderWidget: (
      widget: keyof typeof widgets,
      container: Parameters<typeof render>[1]
    ) => void;
    assetBonnetUrl: string;
    sentry_dsn_react_widgets: string;
    sentry_sample_rate: number;
    ga_tracking_code: string;
    recaptcha_token_refresh_rate: number;
    showArcadePlayNowButton: boolean;
    productLowestPoints: number;
    userId: string;
    arcadeAvailableGamesInfo: ArcadeGameInfo[];
    cplQuizVideoLimit: number;
    videoUnitAdPath: string;
    afterLanderCplQuiz: boolean;
    donationsAsFirstStep: boolean;
    ecommerceProductCollection: ProductCollection;
    cashBackSecretKey: string;
    snhuExtraEntries: number;
    skipVideoAdsQuiz: Optional<boolean>;
    x_pixel_id: string;
    x_add_to_cart_event_id: string;
    reSmsExtraEntries: number;
    reCompanyFormExtraEntries: number;
    reSmsTermsUrl: string;
    enableRestaurantEquippers: boolean;
    reSmsPrivacyPolicyUrl: string;
    reOfferTermsUrl: string;
  }

  interface ArcadeGameInfo {
    url: string;
    modalImageUrl: string;
  }

  interface QuizUtils {
    getBaseURLForCPLForm: (cplFormPath: string | null) => string;
  }

  interface Theme {
    _: null;
    QuizUtils: QuizUtils;
  }

  interface JsTag {
    send: (event: any) => void;
    ckieGet: (event: any) => string;
  }

  type DefaultPixel = (type: string, event: string, data?: Record<string, unknown>) => void;

  interface TikTokPixel {
    track: (event: string, data?: Record<string, unknown>) => void;
  }

  interface Window {
    FKL: FKL;
    theme: Theme;
    Formio: typeof Formio;
    jstag: JsTag;
    trustedFormCertUrlCallback: (certificateUrl: string) => void;
    snaptr: DefaultPixel;
    ttq: TikTokPixel;
    twq: DefaultPixel;
  }

  type Optional<T> = T | undefined;
}
